import { useEffect, useState } from 'react';
import { api, type DnsStatus, type DnsChange, type PaginatedResponse } from '../lib/api';
import s from './DnsStatusPage.module.css';

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function statusDotClass(valid: boolean, hasRecord: boolean): string {
  if (valid) return s.dotValid;
  if (hasRecord) return s.dotInvalid;
  return s.dotMissing;
}

function statusLabel(valid: boolean, hasRecord: boolean): string {
  if (valid) return 'valid';
  if (hasRecord) return 'invalid';
  return 'missing';
}

function policyClass(policy: string | undefined): string {
  switch (policy) {
    case 'reject':
      return s.policyReject;
    case 'quarantine':
      return s.policyQuarantine;
    default:
      return s.policyNone;
  }
}

function changeBadgeClass(oldVal: string, newVal: string): string {
  if (!oldVal && newVal) return s.badgeAdded;
  if (oldVal && !newVal) return s.badgeRemoved;
  return s.badgeModified;
}

function changeBadgeLabel(oldVal: string, newVal: string): string {
  if (!oldVal && newVal) return 'added';
  if (oldVal && !newVal) return 'removed';
  return 'modified';
}

/* -------------------------------------------------------------------------- */
/*  DnsStatusPage                                                              */
/* -------------------------------------------------------------------------- */

export default function DnsStatusPage() {
  const [statuses, setStatuses] = useState<DnsStatus[]>([]);
  const [changes, setChanges] = useState<PaginatedResponse<DnsChange> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      api.dns.status(),
      api.dns.changes({ limit: 20 }),
    ])
      .then(([statusRes, changesRes]) => {
        if (cancelled) return;
        setStatuses(statusRes.statuses);
        setChanges(changesRes);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load DNS status');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <div className={s.loading}>Loading DNS status&hellip;</div>;
  if (error) return <div className={s.error}>{error}</div>;

  return (
    <>
      {/* Header */}
      <div className={s.header}>
        <h1 className={s.title}>DNS Status</h1>
      </div>

      {/* DNS status table */}
      {statuses.length === 0 ? (
        <div className={s.emptyState}>
          No domains configured. Add a domain to see DNS status.
        </div>
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Domain</th>
                <th>SPF Status</th>
                <th>DMARC Policy</th>
                <th>DMARC Status</th>
                <th>Last Checked</th>
              </tr>
            </thead>
            <tbody>
              {statuses.map((dns) => (
                <tr key={dns.domainId}>
                  <td className={s.domainMono}>{dns.domain}</td>
                  <td>
                    <div className={s.statusCell}>
                      <span
                        className={`${s.statusDot} ${statusDotClass(
                          dns.spf.valid,
                          !!dns.spf.record,
                        )}`}
                      />
                      {statusLabel(dns.spf.valid, !!dns.spf.record)}
                    </div>
                  </td>
                  <td>
                    <span className={policyClass(dns.dmarc.policy)}>
                      {dns.dmarc.policy ?? 'none'}
                    </span>
                  </td>
                  <td>
                    <div className={s.statusCell}>
                      <span
                        className={`${s.statusDot} ${statusDotClass(
                          dns.dmarc.valid,
                          !!dns.dmarc.record,
                        )}`}
                      />
                      {statusLabel(dns.dmarc.valid, !!dns.dmarc.record)}
                    </div>
                  </td>
                  <td style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                    &mdash;
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent changes */}
      <h2 className={s.sectionTitle}>Recent Changes</h2>

      {!changes || changes.data.length === 0 ? (
        <div className={s.emptyState}>No DNS changes recorded</div>
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Domain</th>
                <th>Record Type</th>
                <th>Change</th>
                <th>Values</th>
              </tr>
            </thead>
            <tbody>
              {changes.data.map((c) => {
                const domainEntry = statuses.find((d) => d.domainId === c.domainId);
                return (
                  <tr key={c.id}>
                    <td style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                      {new Date(c.detectedAt).toLocaleDateString()}
                    </td>
                    <td className={s.domainMono}>
                      {domainEntry?.domain ?? c.domainId}
                    </td>
                    <td>{c.recordType}</td>
                    <td>
                      <span className={changeBadgeClass(c.oldValue, c.newValue)}>
                        {changeBadgeLabel(c.oldValue, c.newValue)}
                      </span>
                    </td>
                    <td>
                      {c.oldValue && <span className={s.mono}>{c.oldValue}</span>}
                      {c.oldValue && c.newValue && (
                        <span className={s.arrow}>&rarr;</span>
                      )}
                      {c.newValue && <span className={s.mono}>{c.newValue}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
